import { Badge } from '../types';
import { supabase } from './supabaseClient';
import { logError, logInfo } from './logger';
import { triggerPushNotification } from './push-notifications';

type BadgeDefinition = Omit<Badge, 'id' | 'user_id' | 'unlocked_at' | 'users_count'> & {
  autoUnlock: boolean;
};

const BADGE_CATALOG: BadgeDefinition[] = [
  {
    slug: 'founder',
    name: 'Founder',
    description: 'Esteve aqui antes do hype.',
    icon_name: 'Crown',
    rarity_label: 'Rara',
    unlock_hint: 'Reservada para os primeiros apoiadores do app.',
    manual_unlockable: true,
    autoUnlock: true
  },
  {
    slug: 'incentivador',
    name: 'Incentivador',
    description: 'Ajudou a sustentar a criacao do app desde o comeco.',
    icon_name: 'Star',
    rarity_label: 'Rara',
    unlock_hint: 'Liberada para quem marcou a construcao do projeto.',
    manual_unlockable: true,
    autoUnlock: false
  },
  {
    slug: 'maior-incentivadora',
    name: 'Maior Incentivadora',
    description: 'E.T.A...M.M.M.',
    icon_name: 'Flower2',
    rarity_label: 'Rara',
    unlock_hint: 'Para quem me inspirou e esta no coracao do Dopamind.',
    manual_unlockable: true,
    autoUnlock: false
  },
  {
    slug: 'primeiro-passo',
    name: 'Primeiro Passo',
    description: 'Concluiu a primeira task dentro do app.',
    icon_name: 'Zap',
    rarity_label: 'Comum',
    unlock_hint: 'Complete uma task para destravar.',
    manual_unlockable: false,
    autoUnlock: true
  },
  {
    slug: 'ritmo-da-semana',
    name: 'Ritmo da Semana',
    description: 'Alcancou 7 dias de streak.',
    icon_name: 'Flame',
    rarity_label: 'Incomum',
    unlock_hint: 'Mantenha 7 dias seguidos de consistencia.',
    manual_unlockable: false,
    autoUnlock: true
  },
  {
    slug: 'imparavel',
    name: 'Imparavel',
    description: 'Chegou a 30 dias de streak.',
    icon_name: 'Skull',
    rarity_label: 'Epica',
    unlock_hint: 'Chegue a 30 dias de streak real.',
    manual_unlockable: false,
    autoUnlock: true
  },
  {
    slug: 'mente-em-chamas',
    name: 'Mente em Chamas',
    description: 'Fechou 10 sessoes de foco consistentes.',
    icon_name: 'Brain',
    rarity_label: 'Incomum',
    unlock_hint: 'Acumule 10 sessoes de foco.',
    manual_unlockable: false,
    autoUnlock: true
  },
  {
    slug: 'aura-mil',
    name: 'Aura 1000',
    description: 'Ultrapassou a marca de 1000 XP total.',
    icon_name: 'Medal',
    rarity_label: 'Incomum',
    unlock_hint: 'Junte 1000 XP no perfil.',
    manual_unlockable: false,
    autoUnlock: true
  },
  {
    slug: 'centena-em-campo',
    name: 'Centena em Campo',
    description: 'Concluiu 100 tarefas dentro do app.',
    icon_name: 'Award',
    rarity_label: 'Incomum',
    unlock_hint: 'Alcance 100 conclusoes reais de task.',
    manual_unlockable: false,
    autoUnlock: true
  },
  {
    slug: 'mil-vezes',
    name: 'Mil Vezes',
    description: 'Chegou a 1000 tarefas concluidas no app.',
    icon_name: 'Crown',
    rarity_label: 'Lendaria',
    unlock_hint: 'Alcance 1000 conclusoes reais de task.',
    manual_unlockable: false,
    autoUnlock: true
  },
  {
    slug: 'aliado',
    name: 'Aliado',
    description: 'Criou a primeira conexao de amizade no app.',
    icon_name: 'Star',
    rarity_label: 'Comum',
    unlock_hint: 'Tenha pelo menos 1 amizade aceita.',
    manual_unlockable: false,
    autoUnlock: true
  },
  {
    slug: 'combatente',
    name: 'Combatente',
    description: 'Venceu o primeiro duelo PvP.',
    icon_name: 'Skull',
    rarity_label: 'Incomum',
    unlock_hint: 'Ganhe um duelo entre amigos.',
    manual_unlockable: false,
    autoUnlock: true
  },
  {
    slug: 'challenger',
    name: 'Challenger',
    description: 'Entrou no primeiro challenge do app.',
    icon_name: 'Zap',
    rarity_label: 'Comum',
    unlock_hint: 'Participe de um challenge.',
    manual_unlockable: false,
    autoUnlock: true
  },
  {
    slug: 'escolha-do-admin',
    name: 'Escolha do Admin',
    description: 'Recebeu um reconhecimento manual especial do admin.',
    icon_name: 'Crown',
    rarity_label: 'Rara',
    unlock_hint: 'Liberada manualmente pelo admin.',
    manual_unlockable: true,
    autoUnlock: false
  }
];

type BadgeCountRow = {
  slug: string;
  user_id: string;
};

const createBadgeUnlockNotification = async (userId: string, definition: BadgeDefinition): Promise<void> => {
  const { error } = await supabase
    .from('app_notifications')
    .insert({
      user_id: userId,
      type: 'badge_unlock',
      title: 'Nova badge desbloqueada',
      message: `Você recebeu a badge ${definition.name}.`,
      metadata: {
        badge_slug: definition.slug,
        badge_name: definition.name,
        icon_name: definition.icon_name,
        rarity_label: definition.rarity_label || null
      },
      is_read: false
    });

  if (error) {
    logError('badges', `Falha ao criar notificacao da badge ${definition.slug}.`, error);
    return;
  }

  await triggerPushNotification({
    userIds: [userId],
    title: 'Nova badge desbloqueada',
    message: `Você recebeu a badge ${definition.name}.`,
    url: '/#/app/notifications',
    tag: `badge:${definition.slug}`,
    kind: 'badge_unlock',
    metadata: {
      badge_slug: definition.slug,
      badge_name: definition.name
    }
  });
};

export const getBadgeCatalog = (): BadgeDefinition[] => BADGE_CATALOG;

export const getManualBadgeDefinitions = (): BadgeDefinition[] => BADGE_CATALOG.filter((badge) => badge.manual_unlockable);
export const getAllBadgeDefinitions = (): BadgeDefinition[] => BADGE_CATALOG;

const getBadgeDefinition = (slug: string): BadgeDefinition | undefined => BADGE_CATALOG.find((badge) => badge.slug === slug);

const getUnlockedBadgesFromDatabase = async (userId: string): Promise<Badge[]> => {
  const { data, error } = await supabase
    .from('badges')
    .select('*')
    .eq('user_id', userId)
    .order('unlocked_at', { ascending: false });

  if (error) {
    logError('badges', 'Falha ao buscar badges desbloqueadas do usuario.', error);
    return [];
  }

  return (data as Badge[]) || [];
};

const getBadgeCountsMap = async (): Promise<Map<string, number>> => {
  const { data, error } = await supabase
    .from('badges')
    .select('slug,user_id');

  if (error) {
    logError('badges', 'Falha ao buscar contagem global de badges.', error);
    return new Map();
  }

  const counts = new Map<string, Set<string>>();
  ((data as BadgeCountRow[]) || []).forEach((row) => {
    if (!counts.has(row.slug)) {
      counts.set(row.slug, new Set());
    }

    counts.get(row.slug)!.add(row.user_id);
  });

  return new Map(Array.from(counts.entries()).map(([slug, users]) => [slug, users.size]));
};

const grantBadgeIfMissing = async (userId: string, definition: BadgeDefinition, unlockedSlugs: Set<string>): Promise<boolean> => {
  if (unlockedSlugs.has(definition.slug)) {
    return false;
  }

  const { error } = await supabase
    .from('badges')
    .insert({
      user_id: userId,
      slug: definition.slug,
      name: definition.name,
      description: definition.description,
      icon_name: definition.icon_name,
      unlocked_at: new Date().toISOString()
    });

  if (error) {
    logError('badges', `Falha ao liberar badge ${definition.slug}.`, error);
    return false;
  }

  await createBadgeUnlockNotification(userId, definition);

  unlockedSlugs.add(definition.slug);
  return true;
};

export const syncAutomaticBadges = async (userId: string): Promise<void> => {
  try {
    const [
      unlockedBadges,
      profileResult,
      taskCompletionsResult,
      focusSessionsResult,
      challengeParticipantsResult,
      duelWinsResult,
      friendRequestsResult,
      founderPositionResult
    ] = await Promise.all([
      getUnlockedBadgesFromDatabase(userId),
      supabase.from('profiles').select('user_role,total_xp,streak,created_at').eq('id', userId).maybeSingle(),
      supabase.from('task_completions').select('id', { count: 'exact', head: true }).eq('user_id', userId),
      supabase.from('focus_sessions').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('type', 'focus'),
      supabase.from('challenge_participants').select('id', { count: 'exact', head: true }).eq('user_id', userId),
      supabase.from('friend_duels').select('id', { count: 'exact', head: true }).eq('winner_id', userId).eq('status', 'completed'),
      supabase.from('friend_requests').select('id', { count: 'exact', head: true }).eq('status', 'accepted').or(`requester_id.eq.${userId},receiver_id.eq.${userId}`),
      supabase.rpc('get_founder_position', { target_user_id: userId })
    ]);

    const unlockedSlugs = new Set(unlockedBadges.map((badge) => badge.slug));
    const profile = profileResult.data as { user_role?: string; total_xp?: number; streak?: number; created_at?: string | null } | null;
    const unlockQueue: BadgeDefinition[] = [];

    if ((taskCompletionsResult.count || 0) >= 1) {
      unlockQueue.push(getBadgeDefinition('primeiro-passo')!);
    }

    if (Number(profile?.streak || 0) >= 7) {
      unlockQueue.push(getBadgeDefinition('ritmo-da-semana')!);
    }

    if (Number(profile?.streak || 0) >= 30) {
      unlockQueue.push(getBadgeDefinition('imparavel')!);
    }

    if ((focusSessionsResult.count || 0) >= 10) {
      unlockQueue.push(getBadgeDefinition('mente-em-chamas')!);
    }

    if (Number(profile?.total_xp || 0) >= 1000) {
      unlockQueue.push(getBadgeDefinition('aura-mil')!);
    }

    if ((taskCompletionsResult.count || 0) >= 100) {
      unlockQueue.push(getBadgeDefinition('centena-em-campo')!);
    }

    if ((taskCompletionsResult.count || 0) >= 1000) {
      unlockQueue.push(getBadgeDefinition('mil-vezes')!);
    }

    if ((friendRequestsResult.count || 0) >= 1) {
      unlockQueue.push(getBadgeDefinition('aliado')!);
    }

    if ((duelWinsResult.count || 0) >= 1) {
      unlockQueue.push(getBadgeDefinition('combatente')!);
    }

    if ((challengeParticipantsResult.count || 0) >= 1) {
      unlockQueue.push(getBadgeDefinition('challenger')!);
    }

    if (founderPositionResult.error) {
      logError('badges', 'Falha ao calcular a posicao de founder para badges automaticas.', founderPositionResult.error);
    }

    const founderPosition = Array.isArray(founderPositionResult.data)
      ? Number(founderPositionResult.data[0]?.founder_position || 0)
      : Number((founderPositionResult.data as any)?.founder_position || 0);

    if (founderPosition > 0 && founderPosition <= 1000) {
      unlockQueue.push(getBadgeDefinition('founder')!);
    }

    if (profile?.user_role === 'vip') {
      unlockQueue.push(getBadgeDefinition('incentivador')!);
    }

    let unlockedNow = 0;
    for (const definition of unlockQueue.filter(Boolean)) {
      const created = await grantBadgeIfMissing(userId, definition, unlockedSlugs);
      if (created) {
        unlockedNow += 1;
      }
    }

    if (unlockedNow > 0) {
      logInfo('badges', 'Badges automaticas sincronizadas com sucesso.', { userId, unlockedNow });
    }
  } catch (error) {
    logError('badges', 'Falha inesperada ao sincronizar badges automaticas.', error);
  }
};

export const getBadgesForProfile = async (userId: string, options?: { includeLocked?: boolean }): Promise<Badge[]> => {
  const [unlockedBadges, countsMap] = await Promise.all([
    getUnlockedBadgesFromDatabase(userId),
    getBadgeCountsMap()
  ]);

  const unlockedMap = new Map(unlockedBadges.map((badge) => [badge.slug, badge]));

  const catalogBadges = BADGE_CATALOG.map((definition) => {
    const unlockedBadge = unlockedMap.get(definition.slug);

    return {
      id: unlockedBadge?.id || definition.slug,
      slug: definition.slug,
      name: definition.name,
      description: definition.description,
      icon_name: definition.icon_name,
      unlocked_at: unlockedBadge?.unlocked_at,
      users_count: countsMap.get(definition.slug) || 0,
      rarity_label: definition.rarity_label,
      unlock_hint: definition.unlock_hint,
      manual_unlockable: definition.manual_unlockable,
      auto_unlock: definition.autoUnlock,
      admin_badge_mode: definition.autoUnlock ? 'automatico' : 'admin'
    };
  }).filter((badge) => options?.includeLocked ? true : Boolean(badge.unlocked_at));

  const legacyBadges = unlockedBadges
    .filter((badge) => !getBadgeDefinition(badge.slug))
    .map((badge) => ({
      ...badge,
      users_count: countsMap.get(badge.slug) || 0
    }));

  return [...catalogBadges, ...legacyBadges];
};

export type GrantManualBadgeResult = {
  ok: boolean;
  status: 'granted' | 'already_unlocked' | 'badge_not_found' | 'error';
};

export const grantManualBadge = async (userId: string, badgeSlug: string): Promise<GrantManualBadgeResult> => {
  const definition = getBadgeDefinition(badgeSlug);
  if (!definition) {
    return {
      ok: false,
      status: 'badge_not_found'
    };
  }

  const unlockedBadges = await getUnlockedBadgesFromDatabase(userId);
  const unlockedSlugs = new Set(unlockedBadges.map((badge) => badge.slug));
  if (unlockedSlugs.has(badgeSlug)) {
    return {
      ok: false,
      status: 'already_unlocked'
    };
  }

  const granted = await grantBadgeIfMissing(userId, definition, unlockedSlugs);

  if (granted) {
    logInfo('badges', 'Badge manual liberada pelo admin.', { userId, badgeSlug });
    return {
      ok: true,
      status: 'granted'
    };
  }

  return {
    ok: false,
    status: 'error'
  };
};
